import React, { useState, useRef, Fragment, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { Box, Typography, IconButton, Divider } from "@mui/material";
import { FirstPageRounded, LastPageRounded } from "@mui/icons-material";
import { useQueryHandler } from "../../../../hooks/useQueryHandler";
import QueryBox from "./QueryBox";
import { useDispatch, useSelector } from "react-redux";
import {
  getSessionMessages,
  createSession,
} from "../../../../core/repo/sessionRepo";
import { groupMessagesByQuestion } from "../../../AI_Search/AI_Search/utils";
import Messages from "./Messages/Messages";
import StreamMessage from "./StreamMessage";
import { useParams, useHistory } from "react-router-dom";
import { PID_URL } from "../../../../utils/routes";
import { setPidQuerySuccess } from "../../../../core/events/pidEvents";
import Header from "./Header";

const useStyles = makeStyles()((theme) => ({
  container: {
    borderRadius: 12,
    background: theme.palette.background.default,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  iconButton: {
    color: theme.palette.text.primary,
  },
}));

function ChatPanel({
  onIsolationMarkupChange,
  onSelectedIsolationMarkupChange,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { sessionId } = useParams();
  const { query } = useSelector((state) => state.pid);

  // const sessionId = "98ec4bb8-296b-40d2-8b9a-428f90edf076";

  const messagesEndRef = useRef(null);
  const queryBoxContainerRef = useRef();
  const [queryContainerHeight, setQueryContainerHeight] = useState(0);

  async function fetchMessages() {
    setIsLoading(true);
    try {
      const response = await dispatch(getSessionMessages(sessionId));
      const processedMessages = groupMessagesByQuestion(
        response["messages"] || []
      );
      setMessages(processedMessages);
    } catch (e) {
      console.log(e);
    }
    setIsLoading(false);
  }

  useEffect(() => {
    if (sessionId) {
      fetchMessages();
    }
  }, [sessionId]);

  //code to get the height of the query container which is used to decide the height of the last message
  useEffect(() => {
    const queryContainer = queryBoxContainerRef.current;

    if (queryContainer) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const newHeight = entry.contentRect.height;
          setQueryContainerHeight(newHeight);
        }
      });

      resizeObserver.observe(queryContainer);

      return () => {
        resizeObserver.unobserve(queryContainer);
      };
    }
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      handleScroll();
    }
  }, [messages.length]);

  useEffect(() => {
    if (query) {
      handleQuery(query);
    }
  }, [query]);

  useEffect(() => {
    return () => {
      dispatch(setPidQuerySuccess(""));
    };
  }, [dispatch]);

  function handleScroll() {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 10);
  }

  const handleMessageUpdate = (newMessage) => {
    setMessages((prev) => [...prev, newMessage]);
  };

  const { handleQueryIntent, tempQandA, isMetadataLoading, relatedDocs } =
    useQueryHandler(handleMessageUpdate, sessionId);

  const handleQuery = async (
    query,
    taggedFiles = [],
    taggedCategories = []
  ) => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 10);
    handleQueryIntent(query, taggedFiles, taggedCategories);
  };

  return (
    <Box className={classes.container}>
     <Header />
      <Divider />
      <Box px={2} py={1} flex={1} overflow={"auto"}>
        <Messages
          messages={messages}
          isStreaming={tempQandA}
          queryContainerHeight={queryContainerHeight}
          onIsolationMarkupChange={onIsolationMarkupChange}
          onSelectedIsolationMarkupChange={onSelectedIsolationMarkupChange}
        />
        {tempQandA && (
          <StreamMessage
            tempQandA={tempQandA}
            isMetadataLoading={isMetadataLoading}
            queryContainerHeight={queryContainerHeight}
          />
        )}
        <Box ref={messagesEndRef} />
      </Box>
      <Box px={2} py={1}>
        <QueryBox
          isStreaming={tempQandA}
          handleQuery={handleQuery}
          queryBoxContainerRef={queryBoxContainerRef}
        />
      </Box>
    </Box>
  );
}

export default ChatPanel;
